import PropTypes from 'prop-types';
import React, { Fragment, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import styled, { css } from 'styled-components';

import { Box, Flex, SvgIcon } from 'Components/Base';
import { BADGE_VARIANTS, CourseCard } from 'Components/CourseCard';
import { CourseBannerTitle } from 'Components/MyCourses';
import GoToGuidedPracticeButton from 'Containers/GoToGuidedPracticeButton';
import { withConfig } from 'Context/ConfigContext';
import { withOffset } from 'Context/LayoutContext';
import useMyExternalProfile from 'Routes/Classroom/Domain/useMyExternalProfile';
import { EggSpaceOnboardingBanner } from 'Routes/MyCourses/Containers/EggSpaceOnboardingBanner';
import MyPathMilestoneTracker from 'Routes/MyCourses/Containers/MyPathMilestoneTracker';
import { IS_SHOW_MY_COURSE_NAVIGATION_ARROW } from 'Util/Capacitors/constant';
import useCompatibleDisplay from 'Util/Hooks/useCompatibleDisplay';
import { getAppUIVariantConfig } from 'Util/uiVariantSet';

import { COURSE_TYPES } from '../../../../Util/constants';
import BackgroundImage from '../BackgroundImage';
import QuickAccess from '../QuickAccess';

const CourseListContainer = styled(Flex)`
  position: relative;
  * {
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

const CourseBannerWrapper = styled(Flex)`
  & > div:last-child {
    padding-right: 0;
  }
`;

const ArrowButton = styled((props) => (
  <Flex height={'80px'} width={'40px'} alignItems={'center'} justifyContent={'center'} {...props} />
))`
  background: rgba(59, 59, 59, 0.6);
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ isLeft }) =>
    isLeft
      ? css`
          left: 0;
          border-radius: 0px 16px 16px 0px;
        `
      : css`
          right: 0;
          border-radius: 16px 0px 0px 16px;
        `};
  cursor: pointer;
  z-index: 1;
`;

const SCROLL_STATE = {
  START: 'START',
  END: 'END',
  MIDDLE: 'MIDDLE',
};

const CourseList = ({ courses, onCourseCardClick, t, variant, eBookCourseRegex, ...props }) => {
  const showArrow = IS_SHOW_MY_COURSE_NAVIGATION_ARROW && courses.length * 320 > window.innerWidth;
  const courseListRef = useRef();
  const [scrollState, setScrollState] = useState(SCROLL_STATE.START);
  const { CourseBannerCard } = getAppUIVariantConfig();
  const coursesCount = courses.length;

  const scrollX = (offset) => {
    courseListRef.current.scrollLeft += offset;
  };

  const handleScroll = () => {
    const maxSubjectListoffset =
      courseListRef.current?.scrollWidth - courseListRef.current?.offsetWidth;
    const currentOffset = courseListRef.current.scrollLeft;
    if (currentOffset <= 0) {
      setScrollState(SCROLL_STATE.START);
    } else if (currentOffset >= maxSubjectListoffset) {
      setScrollState(SCROLL_STATE.END);
    } else {
      setScrollState(SCROLL_STATE.MIDDLE);
    }
  };

  return (
    <CourseListContainer {...props}>
      {showArrow && (
        <>
          <ArrowButton isLeft={true} onClick={() => scrollX(-350)}>
            <SvgIcon
              icon={SvgIcon.ICON_LIST.BACK}
              size={30}
              color={scrollState === SCROLL_STATE.START ? 'rgba(255, 255, 255, 0.3)' : 'white'}
            />
          </ArrowButton>
          <ArrowButton onClick={() => scrollX(350)}>
            <SvgIcon
              icon={SvgIcon.ICON_LIST.ARROW_RIGHT}
              size={30}
              color={scrollState === SCROLL_STATE.END ? 'rgba(255, 255, 255, 0.3)' : 'white'}
            />
          </ArrowButton>
        </>
      )}
      <CourseBannerWrapper
        ref={courseListRef}
        flex={coursesCount === 1 ? 1 : 'unset'}
        style={{ overflowX: 'auto' }}
        id="course-banners"
        onScroll={handleScroll}
      >
        {courses.map((course, index) => {
          const { courseInfo, classroomTime } = course;
          const {
            remainingTime = 0,
            classroomTimeInfo,
            type: timeType,
            isValidRemainingTime,
          } = classroomTime || {};
          const { courseType, courseCode } = classroomTimeInfo || {};
          const {
            classroomMode,
            coverImageUrl,
            id,
            title,
            courseCode: courseCodeInfo,
            classroomProgress: { progressPercent, expiryDate, lastEnterAt },
            totalActiveLearningModes,
            mode,
          } = courseInfo || {};
          const defaultImageUrl =
            variant === 'ANY'
              ? '/assets/images/defaultCourseThumbnail/empty-thumbnail-ANY.png'
              : '/assets/images/defaultCourseThumbnail/empty-thumbnail.png';
          const badgeVariant = eBookCourseRegex?.test(courseCodeInfo)
            ? BADGE_VARIANTS.EBOOK
            : totalActiveLearningModes > 1
            ? BADGE_VARIANTS.LEARNING_MODE
            : BADGE_VARIANTS.NONE;
          const name = courseCodeInfo
            ? `course-card-banner-course-code-${courseCodeInfo}`
            : `course-card-banner`;
          return (
            <Box pr={3} width={1} minWidth="auto" key={course.id}>
              <CourseCard
                CourseCardVariant={CourseBannerCard}
                badgeVariant={badgeVariant}
                imageUrl={coverImageUrl}
                defaultImageUrl={defaultImageUrl}
                title={title}
                time={remainingTime}
                timeType={timeType}
                isValidRemainingTime={isValidRemainingTime}
                lastEnter={lastEnterAt}
                expDate={expiryDate}
                onClick={() => onCourseCardClick(id, mode)}
                progress={Math.floor(progressPercent ?? 0)}
                containerProps={coursesCount > 1 ? { width: '272px' } : { maxWidth: '343px' }}
                footer={
                  courseType === COURSE_TYPES.PACK &&
                  t('COURSE_CARD.FOOTER.DEFAULT', { packName: courseCode })
                }
                trialMode={classroomMode === 'TRIAL'}
                name={name}
                totalActiveLearningModes={totalActiveLearningModes}
              />
            </Box>
          );
        })}
      </CourseBannerWrapper>
    </CourseListContainer>
  );
};

const CourseBanner = ({ appConfig, courses, onCourseCardClick, t, user, ...props }) => {
  const {
    uiVariant,
    myCourses: myCoursesConfig,
    myPath: myPathConfig,
    guidedPractice: guidedPracticeConfig,
    eBookCourseRegex = /^.*E$/,
    tungKai: tungKaiConfig,
  } = appConfig;
  const { isMobile } = useCompatibleDisplay();
  const { data: myExternalProfileData } = useMyExternalProfile({ fetchPolicy: 'cache-only' });

  const maxRecentCoursesCount = myCoursesConfig?.maxRecentCoursesCount ?? 5;
  const bannerUrl = myCoursesConfig?.bannerUrl;
  const enableQuickAccess = !!myCoursesConfig?.enableQuickAccess;
  const disableCourseBannerTitle = !!myCoursesConfig?.disableCourseBannerTitle;
  const enableSearchMyCourse = !!myCoursesConfig?.enableSearchMyCourse;
  const enableMyPathV2 = !!myPathConfig?.enableMyPathV2;

  const enableGuidedPracticeButton = !!guidedPracticeConfig?.enableGuidedPracticeButton;
  // NOTE: `isGuidedPracticeButtonEnabled` is available from version 2.51.0 and above
  const isGuidedPracticeButtonEnabled = isMobile && enableGuidedPracticeButton;

  const providerName = myExternalProfileData?.myExternalProfile?.providerName ?? '';
  const enableFeatureFreeTrialBannerByBU = myCoursesConfig?.enableFeatureFreeTrialBannerByBU ?? [];

  const shouldShowTungKaiOnboarding =
    tungKaiConfig?.eggSpaceOnboarding?.enable &&
    !tungKaiConfig.eggSpaceOnboarding.disabledOnboardingUserIds.includes(user.id) &&
    enableFeatureFreeTrialBannerByBU.includes(providerName);

  return (
    <BackgroundImage px={0} pb={5} $backgroundImage={bannerUrl}>
      {shouldShowTungKaiOnboarding && (
        <EggSpaceOnboardingBanner
          courses={courses}
          tips={tungKaiConfig.eggSpaceOnboarding.pendingCourseEnrollmentModal.tips ?? []}
          subtitle={tungKaiConfig.eggSpaceOnboarding.subtitle}
        />
      )}
      {isGuidedPracticeButtonEnabled && (
        <Box px={2} mt={3}>
          <GoToGuidedPracticeButton />
        </Box>
      )}
      {enableMyPathV2 ? (
        <MyPathMilestoneTracker />
      ) : (
        enableQuickAccess && <QuickAccess px={2} mt={enableSearchMyCourse ? 5 : 0} />
      )}
      {courses.length > 0 && (
        <Fragment>
          {disableCourseBannerTitle ? (
            /** Show only subtitle on Anywhere*/
            <CourseBannerTitle subTitle={t('BANNER.TITLE2')} />
          ) : (
            <CourseBannerTitle title={t('BANNER.TITLE1')} subTitle={t('BANNER.TITLE2')} />
          )}
          <CourseList
            px={2}
            courses={courses.slice(0, maxRecentCoursesCount)}
            onCourseCardClick={onCourseCardClick}
            t={t}
            variant={uiVariant}
            eBookCourseRegex={eBookCourseRegex}
          />
        </Fragment>
      )}
    </BackgroundImage>
  );
};

CourseBanner.propTypes = {
  courses: PropTypes.array,
  onCourseCardClick: PropTypes.func,
  user: PropTypes.object,
};

CourseBanner.defaultProps = {
  courses: [],
  onCourseCardClick: () => {},
};

export { CourseList };
export default compose(withConfig, withTranslation('myCourses'), withOffset)(CourseBanner);
